import { changePlayPauseBtn, updateProgressBar } from 'components/mediaControls/helpers';
import { addClass, generate6DigitUUID, hasClass, removeClass } from 'helpers/helpers';
import { ENGT_CHAT } from 'js/bot.model';
import { MediaControlsTemplate } from './mediaControls.template';

export default class MediaControls {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initEventHandlers();
	}

	initData(propData = {}) {
		this.data = {
			id: propData.message_id || generate6DigitUUID(),
			bgColor: 'transparent',
			color: ENGT_CHAT.get('config.botResponseTextColor'),
			showControls: propData?.showControls,
		};
		this.setter({
			...propData,
		});
	}

	initWrapper() {
		const { bgColor, isFooter, showControls = true } = this.data;
		const wrapper = document.createElement('div');
		wrapper.classList.add('engt-media-controls');
		!showControls && wrapper.classList.add('engt-media-controls-hidden');
		wrapper.setAttribute('aria-label', 'media controls');
		isFooter && wrapper.classList.add('engt-preview-footer');
		wrapper.style.background = bgColor;
		wrapper.innerHTML = MediaControlsTemplate(this.data);

		this.html = wrapper;
	}

	updateTimeBar(media, timeBar) {
		const elapsedTime = (Math.floor(media.currentTime / 60) + (media.currentTime % 60) / 100).toFixed(2);
		const time = `${elapsedTime} / ${(Math.floor(media.duration / 60) + (media.duration % 60) / 100).toFixed(2)}`;
		timeBar.innerHTML = `${time}`;
	}

	changeVolumeBtn(media, changeToBtn, id) {
		const volume = document.getElementById(`engt-vol-${id}`);
		const mute = document.getElementById(`engt-mute-${id}`);
		const volBar = document.getElementById(`engtVolProgress-${id}`);
		if (changeToBtn === 'mute') {
			media.muted = true;
			volume.classList.add('engt-hidden');
			volBar.classList.add('engt-hidden');
			mute.classList.remove('engt-hidden');
		} else {
			media.muted = false;
			mute.classList.add('engt-hidden');
			volume.classList.remove('engt-hidden');
			volBar.classList.remove('engt-hidden');
		}
	}

	initEventHandlers() {
		const { id, mediaId, isFooter, mediaNode } = this.data;

		setTimeout(() => {
			const media = document.getElementById(mediaId) || mediaNode;
			const btnId = isFooter ? 'control' : id;
			const play = document.getElementById(`engt-play-${btnId}`);
			const pause = document.getElementById(`engt-pause-${btnId}`);
			const volume = document.getElementById(`engt-vol-${btnId}`);
			const mute = document.getElementById(`engt-mute-${btnId}`);
			const rate = document.getElementById(`engt-rate-${btnId}`);
			const progressBar = document.getElementById(`engtMediaProgress-${btnId}`);
			const volBar = document.getElementById(`engtVolProgress-${btnId}`);
			const timeBar = document.getElementById(`engtMediaTime-${btnId}`);
			const speedWrapper = document.getElementById(`engtSpeed-${btnId}`);
			const rateContainer = document.getElementById(`engtRateWrapper-${btnId}`);
			const rateOptions = Array.from(document.getElementById(`engtRateItems-${btnId}`)?.children || []);

			progressBar?.focus();
			/* playback rate listeners */
			rate.addEventListener('click', () => {
				if (hasClass(speedWrapper, 'engt-playback-rate-active')) {
					rateOptions.map((option) => {
						option.setAttribute('tabindex', -1);
					});
					removeClass(speedWrapper, 'engt-playback-rate-active');
					removeClass(rateContainer, 'engt-playback-rate-wrapper-active');
				} else {
					rateOptions.map((option) => {
						option.setAttribute('tabindex', 0);
					});
					addClass(rateContainer, 'engt-playback-rate-wrapper-active');
					addClass(speedWrapper, 'engt-playback-rate-active');
				}
			});

			speedWrapper.addEventListener('mouseleave', () => {
				rateOptions.map((option) => {
					option.setAttribute('tabindex', -1);
				});
				removeClass(speedWrapper, 'engt-playback-rate-active');
				removeClass(rateContainer, 'engt-playback-rate-wrapper-active');
			});

			rateOptions.map((option) =>
				option.addEventListener('click', (e = event) => {
					rateOptions.map((option) => {
						option.setAttribute('tabindex', -1);
					});
					media.playbackRate = e.target.getAttribute('data-value');
					removeClass(speedWrapper, 'engt-playback-rate-active');
					removeClass(rateContainer, 'engt-playback-rate-wrapper-active');
				})
			);

			/* media button listeners */
			play.addEventListener('click', () => {
				changePlayPauseBtn(media, 'pause', btnId);
			});
			pause.addEventListener('click', () => {
				changePlayPauseBtn(media, 'play', btnId);
			});
			volume.addEventListener('click', () => {
				this.changeVolumeBtn(media, 'mute', btnId);
			});
			mute.addEventListener('click', () => {
				this.changeVolumeBtn(media, 'volume', btnId);
			});
			progressBar.addEventListener('change', (e = $event) => {
				updateProgressBar(e.target.value, btnId, 'engtMediaProgress');
				const elapsedTime = (Math.floor(media.currentTime / 60) + (media.currentTime % 60) / 100).toFixed(2);
				progressBar.setAttribute(
					'aria-label',
					`elapsed time: ${elapsedTime}, media time scrubber: ${timeBar.innerText}`
				);
				media.currentTime = Math.floor((e.target.value * media.duration) / 100).toFixed(2);
			});
			volBar.addEventListener('change', (e = $event) => {
				media.volume = e.target.value;
				updateProgressBar(e.target.value * 100, btnId, 'engtVolProgress');
			});

			/* media event listeners */
			media.onloadedmetadata = () => {
				this.updateTimeBar(media, timeBar);
			};
			media.addEventListener('ended', () => {
				changePlayPauseBtn(media, 'play', btnId);
				media.currentTime = 0;
			});
			media.addEventListener('playing', () => {
				changePlayPauseBtn(media, 'pause', btnId);
			});
			media.addEventListener('pause', () => {
				changePlayPauseBtn(media, 'play', btnId);
			});
			media.addEventListener('ratechange', () => {
				const selectedRate = document.getElementById(`engtRateText-${btnId}`);
				selectedRate.innerHTML = `${media.playbackRate}x`;
			});
			media.addEventListener('volumechange', () => {
				updateProgressBar(media.volume * 100, btnId, 'engtVolProgress');
			});
			media.addEventListener(
				'timeupdate',
				() => {
					this.updateTimeBar(media, timeBar);
					const per = Math.floor((100 / media.duration) * media.currentTime);
					const elapsedTime = (Math.floor(media.currentTime / 60) + (media.currentTime % 60) / 100).toFixed(
						2
					);
					progressBar.setAttribute(
						'aria-label',
						`elapsed time: ${elapsedTime}, media time scrubber: ${timeBar.innerText}`
					);
					updateProgressBar(per, btnId, 'engtMediaProgress');
					progressBar.value = per;
				},
				false
			);
		}, 300);
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	render() {
		return this.html;
	}
}
